import * as z from 'zod';
import { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
import type { Country } from 'react-phone-number-input';
import {
  dateOnlyOf,
  formatDayMonthYear,
  orgCalendarDate,
  parseIsoDate,
  toDateOnlyValue,
} from '@/lib/utils/custom-date/custom-date';

/** Pure helpers shared by the service desk forms and tables. */

/** The legacy forms collect first/last name separately and persist a single `Name`. */
export const splitName = (fullName: string | undefined) => {
  const [firstName = '', ...rest] = (fullName ?? '').trim().split(/\s+/);
  return { firstName, lastName: rest.join(' ') };
};

export const joinName = (firstName: string, lastName: string) =>
  `${firstName.trim()} ${lastName.trim()}`.trim();

/**
 * A moment (`AppliedDateUtc`, `ApprovedDateUtc`) -> its calendar day as `21-Sep-2026` (legacy table style), taken in the
 * record's `Timezone` (the organization's zone for old rows without one), never the device's.
 */
export const formatMomentDay = (value?: string | null, timeZone?: string | null, fallback = '-') =>
  formatDayMonthYear(parseIsoDate(orgCalendarDate(value, timeZone || undefined)), fallback);

/**
 * A date-only field (`DateOfBirth`, `TravelStartDate`, ...: the picked day at 00:00:00 UTC) ->
 * `21-Sep-2026`, read by its date part and never converted to a zone.
 */
export const formatDateOnlyShort = (value?: string | null, fallback = '-') =>
  formatDayMonthYear(parseIsoDate(dateOnlyOf(value)), fallback);

/** A date-only field -> value accepted by `<input type="date">` ("YYYY-MM-DD"). */
export const toDateInputValue = (value?: string | null) => dateOnlyOf(value);

/**
 * `<input type="date">` bounds -> a range clause on a date-only field: `from` inclusive, `to`
 * inclusive (as `$lt` the next day at 00:00:00 UTC). Either side may be empty.
 */
export const dateOnlyFromClause = (from: string) => {
  const value = toDateOnlyValue(from);
  return value ? { $gte: value } : undefined;
};

export const dateOnlyToClause = (to: string) => {
  const value = toDateOnlyValue(to);
  if (!value) return undefined;
  const next = new Date(value);
  next.setUTCDate(next.getUTCDate() + 1);
  return { $lt: next.toISOString() };
};

/** Parses a JSON string column, falling back when it is empty or malformed. */
export const parseJson = <T>(value: string | undefined | null, fallback: T): T => {
  if (!value) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
};

export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * A required text field of at most `max` characters: the same limits the service desk workflows check
 * (blocks/workflows/service-desk/build.mjs), so a long value is a field error, not a refused save.
 */
export const requiredText = (requiredKey: string, max: number) =>
  z.string().trim().min(1, { message: requiredKey }).max(max, { message: 'TEXT_TOO_LONG' });

export const optionalText = (max: number) =>
  z.string().trim().max(max, { message: 'TEXT_TOO_LONG' });

export const requiredEmail = (requiredKey: string) =>
  requiredText(requiredKey, 200).regex(EMAIL_PATTERN, { message: 'EMAIL_INVALID' });

/** The countries the legacy service desk forms accepted phone numbers from (`preferredCountries`). */
export const SERVICE_DESK_PHONE_COUNTRIES: Country[] = ['BD', 'BT', 'CH', 'DE', 'NP', 'XK'];

const isServiceDeskCountry = (value: string) => {
  const country = parsePhoneNumber(value)?.country;
  return Boolean(country && SERVICE_DESK_PHONE_COUNTRIES.includes(country));
};

/** A required international phone number of one of the service desk countries. */
export const requiredPhoneSchema = (requiredKey: string) =>
  z
    .string()
    .trim()
    .min(1, { message: requiredKey })
    .refine((value) => isValidPhoneNumber(value) && isServiceDeskCountry(value), {
      message: 'PHONE_NUMBER_INVALID',
    });

/** Which slice of the delivered applications one page of a "delivered last" list shows. */
export interface DeliveredSlice {
  /** The delivered-list pages (1-based, `pageSize` each) that hold the slice; empty when none. */
  pages: number[];
  /** Start and end (exclusive) of the slice within the concatenated items of `pages`. */
  start: number;
  end: number;
}

/**
 * The Business Card and ID Card lists sort delivered applications after the open ones, each part
 * newest first (legacy `$sort: { _deliveredSort: 1, AppliedDate: -1 }`). The Data Gateway cannot sort
 * on a computed key, so the list reads the open and the delivered applications separately: page
 * `pageNo` shows the open ones from that same page of the open list, then fills the rest with the
 * delivered ones that follow all `openCount` open ones. This works out which delivered-list pages
 * (at most two) hold that run and where it sits in them.
 */
export const planDeliveredSlice = (
  openCount: number,
  pageNo: number,
  pageSize: number
): DeliveredSlice => {
  const from = Math.max((pageNo - 1) * pageSize - openCount, 0);
  const to = Math.max(pageNo * pageSize - openCount, 0);
  if (to <= from) return { pages: [], start: 0, end: 0 };
  const firstPage = Math.floor(from / pageSize) + 1;
  const lastPage = Math.floor((to - 1) / pageSize) + 1;
  const offset = (firstPage - 1) * pageSize;
  const pages = Array.from({ length: lastPage - firstPage + 1 }, (_, i) => firstPage + i);
  return { pages, start: from - offset, end: to - offset };
};

/** The fulfilment pipeline shared by Business Card and ID Card applications. */
export const FULFILMENT_STATUS_OPTIONS = [
  { value: 'Pending', labelKey: 'PENDING' },
  { value: 'InProgress', labelKey: 'IN_PROGRESS' },
  { value: 'Completed', labelKey: 'COMPLETED' },
  { value: 'Delivered', labelKey: 'DELIVERED' },
];
